/**
 * Message query operations - Update
 */

import { getSupabaseClient } from '../../supabase-client.js';
import type { StoredMessage } from '../types.js';
import { logger } from '../../../logger.js';

/**
 * Update message parts
 */
export async function updateMessageParts(
  messageId: string,
  parts: Array<Record<string, unknown>>
): Promise<StoredMessage> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .from('meridian_messages')
      .update({ parts })
      .eq('id', messageId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data as StoredMessage;
  } catch (error) {
    logger.error('error updating message parts', error, { op: 'updateMessageParts' });
    throw error;
  }
}

/**
 * Update message metadata
 */
export async function updateMessageMetadata(
  messageId: string,
  metadata: Record<string, unknown>
): Promise<StoredMessage> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .from('meridian_messages')
      .update({ metadata })
      .eq('id', messageId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data as StoredMessage;
  } catch (error) {
    logger.error('error updating message metadata', error, { op: 'updateMessageMetadata' });
    throw error;
  }
}

/**
 * Update the parts of a message addressed by its STABLE UIMessage id.
 *
 * `loadChat` hands the client `message_id` (not the row's uuid PK), so callers
 * that read history and then write back — the Slack tool-result appender — hold
 * a `message_id`, never an `id`. Keying the update on (chat_id, message_id) is
 * what makes that round-trip work.
 */
export async function updateMessagePartsByMessageId(
  chatId: string,
  messageId: string,
  parts: Array<Record<string, unknown>>
): Promise<StoredMessage | null> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .from('meridian_messages')
      .update({ parts })
      .eq('chat_id', chatId)
      .eq('message_id', messageId)
      .select()
      .maybeSingle();

    if (error) {
      throw error;
    }

    return (data as StoredMessage | null) ?? null;
  } catch (error) {
    logger.error('error updating message parts by message id', error, {
      op: 'updateMessagePartsByMessageId',
      chatId,
      messageId,
    });
    throw error;
  }
}
